import { useMemo } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { cellSx } from '../../../components/ui/tableTemplate/cellRenderers';
import { TruncatedCell } from '../../../components/ui/tableTemplate/TruncatedCell';
import type { TableColumn } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { COLUMN_WIDTH } from '../../../shared/constants';
import { EventInboxStatusBadge } from '../components/EventInboxStatusBadge';
import { formatDateTime } from '../../../lib/date/formatDateTime';
import { STATUS_META, STATUS_ORDER, type EventInboxListItem } from '../eventInboxTypes';

const I18N_PREFIX = 'eventInbox:columns';

/**
 * Column definitions for the Event Inbox table. Filtering is inline in the column
 * headers — each backend-filterable column carries a `filter` def and the shared
 * `TableTemplate`/`ColumnFilterInput` render the input, while `useTableState`/
 * `appendFilterParams` handle state + serialization (same path as employees).
 * Filterable columns mirror the backend config (HOMCO-772): `status` (list),
 * `message_id`/`exchange`/`failed_rule` (text → like), `received_at`/`processed_at`
 * (date). `attempts` and `last_error` are not backend-filterable, so carry no filter.
 * Columns are **not sortable**: the API always orders by `received_at desc` and
 * ignores other sort keys, so clickable sort headers would be dead UI (design D7).
 */
export function useEventInboxColumns(): TableColumn<EventInboxListItem>[] {
  const { t } = useTranslation('eventInbox');

  return useMemo(() => {
    // Status options derive from the shared status constants — codes from
    // STATUS_ORDER, labels resolved via i18n. No hardcoded values.
    const statusOptions = STATUS_ORDER.map(status => ({
      code: status,
      libelle: t(STATUS_META[status].labelKey),
    }));

    return [
      {
        key: 'status',
        labelKey: `${I18N_PREFIX}.status`,
        width: COLUMN_WIDTH.text,
        filter: { kind: 'list', options: statusOptions },
        render: (row: EventInboxListItem) => <EventInboxStatusBadge status={row.status} />,
      },
      {
        key: 'message_id',
        labelKey: `${I18N_PREFIX}.messageId`,
        width: COLUMN_WIDTH.multiValue,
        filter: { kind: 'text' },
        render: (row: EventInboxListItem) => (
          <TruncatedCell
            text={row.message_id || '—'}
            sx={{ ...cellSx, fontFamily: 'monospace', fontSize: '0.8rem' }}
          />
        ),
      },
      {
        key: 'exchange',
        labelKey: `${I18N_PREFIX}.exchange`,
        width: COLUMN_WIDTH.text,
        filter: { kind: 'text' },
        render: (row: EventInboxListItem) => (
          <TruncatedCell text={row.exchange || '—'} sx={cellSx} />
        ),
      },
      {
        key: 'attempts',
        labelKey: `${I18N_PREFIX}.attempts`,
        width: COLUMN_WIDTH.number,
        align: 'right',
        type: 'number',
        render: (row: EventInboxListItem) => (
          <Typography sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>
            {row.attempts}
          </Typography>
        ),
      },
      {
        key: 'failed_rule',
        labelKey: `${I18N_PREFIX}.failedRule`,
        width: COLUMN_WIDTH.text,
        filter: { kind: 'text' },
        // Structured "why it failed"; null = structural failure → em dash.
        render: (row: EventInboxListItem) =>
          row.failed_rule ? (
            <TruncatedCell text={row.failed_rule} sx={cellSx} />
          ) : (
            <Typography sx={{ ...cellSx, color: 'text.label' }}>—</Typography>
          ),
      },
      {
        key: 'last_error',
        labelKey: `${I18N_PREFIX}.lastError`,
        width: COLUMN_WIDTH.multiValue,
        render: (row: EventInboxListItem) =>
          row.last_error ? (
            <Tooltip title={row.last_error} placement="top">
              <Typography
                noWrap
                sx={{ ...cellSx, color: 'error.main', maxWidth: COLUMN_WIDTH.multiValue }}
              >
                {row.last_error}
              </Typography>
            </Tooltip>
          ) : (
            <Typography sx={{ ...cellSx, color: 'text.label' }}>—</Typography>
          ),
      },
      {
        key: 'received_at',
        labelKey: `${I18N_PREFIX}.receivedAt`,
        width: COLUMN_WIDTH.date,
        filter: { kind: 'date' },
        render: (row: EventInboxListItem) => (
          <TruncatedCell
            text={formatDateTime(row.received_at)}
            sx={{ ...cellSx, whiteSpace: 'nowrap' }}
          />
        ),
      },
      {
        key: 'processed_at',
        labelKey: `${I18N_PREFIX}.processedAt`,
        width: COLUMN_WIDTH.date,
        filter: { kind: 'date' },
        render: (row: EventInboxListItem) => (
          <TruncatedCell
            text={formatDateTime(row.processed_at)}
            sx={{ ...cellSx, whiteSpace: 'nowrap' }}
          />
        ),
      },
    ];
  }, [t]);
}
